/**
 * Overlay path classification for committed Studio readiness.
 * Setup/prequal/link surfaces stay awaiting_user; only a current room path
 * may publish overlay success. Per rule: stay-in-scope — path facts only,
 * no remaining-budget or human-wait clock.
 */

import type { BootAttemptObservation } from '#~/depot/types.js';

const normalizePath = (pathname: string): string =>
  pathname.startsWith('/') ? pathname : `/${pathname}`;

/** Operator-action routes: overlay may dismiss, but that is not Studio ready. */
export const isAwaitingUserBootPath = (pathname: string): boolean => {
  const path = normalizePath(pathname);
  return (
    path.startsWith('/agent/setup') ||
    path === '/agent/linking' ||
    /^\/agent\/[^/]+\/link(?:\/|$)/.test(path) ||
    /^\/agent\/[^/]+\/wait(?:\/|$)/.test(path) ||
    /^\/agent\/[^/]+$/.test(path) ||
    /\/inspect(?:\/|$)/.test(path) ||
    path.startsWith('/invite') ||
    path.startsWith('/build-info')
  );
};

/** Current room or room-boot shell, not an action/setup surface. */
export const isQualifiedStudioBootPath = (pathname: string): boolean => {
  const path = normalizePath(pathname);
  if (isAwaitingUserBootPath(path)) {
    return false;
  }
  return /^\/room\/[^/]+/.test(path);
};

/** Room id from `/room/:id` (and `/room/:id/boot`). Null on action/setup paths. */
export const studioRoomIdFromPath = (pathname: string): string | null => {
  const match = normalizePath(pathname).match(/^\/room\/([^/]+)/);
  return match?.[1] ?? null;
};

/**
 * Overlay may publish ready only when the current route, access, inventory,
 * and room/account facts agree. Unknown access is not already-usable.
 * Empty/viewer/degraded rooms still qualify without camera frames.
 */
export const isQualifiedCommittedStudioReady = ({
  pathname,
  observation,
  currentAccountId
}: {
  pathname: string;
  observation: Pick<
    BootAttemptObservation,
    'access' | 'roomFacts' | 'roomId' | 'accountId' | 'outcome'
  >;
  currentAccountId: string | null;
}): boolean => {
  if (observation.outcome === 'failed') {
    return false;
  }
  if (!isQualifiedStudioBootPath(pathname)) {
    return false;
  }
  const pathRoom = studioRoomIdFromPath(pathname);
  if (!pathRoom || observation.roomId !== pathRoom) {
    return false;
  }
  if (observation.access !== 'viewer' && observation.access !== 'writer') {
    return false;
  }
  // The room publisher must actually have observed this room; an empty Studio
  // qualifies, an unobserved one does not.
  if (observation.roomFacts !== 'observed') {
    return false;
  }
  if (observation.accountId && currentAccountId && observation.accountId !== currentAccountId) {
    return false;
  }
  return true;
};
